import type { ReactNode } from 'react'
import { X } from 'lucide-react'

import { Button, Panel } from '@/components/ui'
import { cn } from '@/lib/utils'

export function SideSheet({
  children,
  description,
  onClose,
  open,
  title,
  widthClassName = 'max-w-2xl',
}: {
  children: ReactNode
  description?: string
  onClose: () => void
  open: boolean
  title: string
  widthClassName?: string
}) {
  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        aria-label="Закрыть окно"
        className="absolute inset-0 bg-ink-950/35 backdrop-blur-sm"
        onClick={onClose}
        type="button"
      />
      <section
        aria-label={title}
        className={cn(
          'relative h-full w-full overflow-y-auto border-l border-white/70 bg-slate-100 p-4 shadow-2xl lg:p-6',
          widthClassName,
        )}
      >
        <Panel className="min-h-full">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-ink-950">{title}</h2>
              {description ? <p className="text-sm text-ink-500">{description}</p> : null}
            </div>
            <button
              aria-label="Закрыть"
              className="rounded-2xl p-2 text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
              onClick={onClose}
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {children}
        </Panel>
      </section>
    </div>
  )
}

export function ConfirmDialog({
  cancelLabel = 'Отмена',
  confirmLabel = 'Подтвердить',
  description,
  onCancel,
  onConfirm,
  open,
  title,
  tone = 'danger',
}: {
  cancelLabel?: string
  confirmLabel?: string
  description: string
  onCancel: () => void
  onConfirm: () => void
  open: boolean
  title: string
  tone?: 'danger' | 'primary'
}) {
  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        aria-label="Закрыть подтверждение"
        className="absolute inset-0 bg-ink-950/35 backdrop-blur-sm"
        onClick={onCancel}
        type="button"
      />
      <Panel className="relative w-full max-w-lg">
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-ink-950">{title}</h3>
          <p className="text-sm text-ink-500">{description}</p>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={onConfirm} tone={tone === 'danger' ? 'danger' : 'primary'}>
            {confirmLabel}
          </Button>
          <Button onClick={onCancel} tone="ghost">
            {cancelLabel}
          </Button>
        </div>
      </Panel>
    </div>
  )
}
